/**
 * Notifications Store
 * Pinia store for notification management
 */

import { api } from "@/utils/api";
import { socketService } from "@/utils/socket";
import { WSEvent } from "@shared/constants";
import type {
  Notification,
  NotificationPreferences,
  NotificationStats,
  NotificationStatus,
  NotificationType,
  UpdateNotificationInput,
  UpdateNotificationPreferencesInput,
  WSEventPayload,
} from "@shared/types";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export const useNotificationsStore = defineStore("notifications", () => {
  // State
  const notifications = ref<Notification[]>([]);
  const preferences = ref<NotificationPreferences | null>(null);
  const stats = ref<NotificationStats | null>(null);
  const isLoading = ref(false);

  // Getters
  const unreadNotifications = computed(() =>
    notifications.value.filter((n) => n.status === "unread"),
  );

  const unreadCount = computed(() => unreadNotifications.value.length);

  const notificationsByType = computed(() => {
    const byType: Partial<Record<NotificationType, Notification[]>> = {};

    notifications.value.forEach((notification) => {
      if (!byType[notification.type]) {
        byType[notification.type] = [];
      }
      byType[notification.type]?.push(notification);
    });

    return byType;
  });

  const isSoundEnabled = computed(
    () => preferences.value?.enableSound || false,
  );

  const sortedNotifications = computed(() =>
    [...notifications.value].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    ),
  );

  // Actions
  /**
   * Fetch notifications with optional filtering
   */
  const fetchNotifications = async (options?: {
    status?: NotificationStatus;
    type?: NotificationType;
    limit?: number;
    offset?: number;
  }) => {
    isLoading.value = true;

    try {
      const params = new URLSearchParams();

      if (options?.status) params.set("status", options.status);
      if (options?.type) params.set("type", options.type);
      if (options?.limit) params.set("limit", options.limit.toString());
      if (options?.offset) params.set("offset", options.offset.toString());

      const data = await api.get<Notification[]>(
        `/api/notifications?${params.toString()}`,
      );
      notifications.value = data;
    } catch (error: unknown) {
      console.error("Failed to fetch notifications:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch notification statistics
   */
  const fetchStats = async () => {
    try {
      const data = await api.get<NotificationStats>("/api/notifications/stats");
      stats.value = data;
    } catch (error: unknown) {
      console.error("Failed to fetch notification stats:", error);
      throw error;
    }
  };

  /**
   * Fetch user preferences
   */
  const fetchPreferences = async () => {
    try {
      const data = await api.get<NotificationPreferences>(
        "/api/notifications/preferences",
      );
      preferences.value = data;
    } catch (error: unknown) {
      console.error("Failed to fetch notification preferences:", error);
      throw error;
    }
  };

  /**
   * Update notification (mark as read/archived)
   */
  const updateNotification = async (
    id: number,
    input: UpdateNotificationInput,
  ) => {
    try {
      const updated = await api.patch<Notification>(
        `/api/notifications/${id}`,
        input,
      );

      const index = notifications.value.findIndex((n) => n.id === id);
      if (index !== -1) {
        notifications.value[index] = updated;
      }

      // Refresh stats after update
      await fetchStats();

      return updated;
    } catch (error: unknown) {
      console.error("Failed to update notification:", error);
      throw error;
    }
  };

  /**
   * Mark notification as read
   */
  const markAsRead = async (id: number) => {
    return updateNotification(id, { status: "read" });
  };

  /**
   * Mark notification as archived
   */
  const markAsArchived = async (id: number) => {
    return updateNotification(id, { status: "archived" });
  };

  /**
   * Archive notification (alias for markAsArchived)
   */
  const archiveNotification = async (id: number) => {
    return markAsArchived(id);
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = async () => {
    try {
      await api.post<{ message: string; count: number }>(
        "/api/notifications/mark-all-read",
        {},
      );

      // Update local state
      notifications.value = notifications.value.map((n) => ({
        ...n,
        status: "read" as const,
        readAt: new Date().toISOString(),
      }));

      // Refresh stats
      await fetchStats();
    } catch (error: unknown) {
      console.error("Failed to mark all notifications as read:", error);
      throw error;
    }
  };

  /**
   * Delete notification
   */
  const deleteNotification = async (id: number) => {
    try {
      await api.delete(`/api/notifications/${id}`);

      // Remove from local state
      notifications.value = notifications.value.filter((n) => n.id !== id);

      // Refresh stats
      await fetchStats();
    } catch (error: unknown) {
      console.error("Failed to delete notification:", error);
      throw error;
    }
  };

  /**
   * Delete all archived notifications
   */
  const deleteArchivedNotifications = async () => {
    try {
      await api.delete<{ message: string; count: number }>(
        "/api/notifications/archived/all",
      );

      // Remove archived from local state
      notifications.value = notifications.value.filter(
        (n) => n.status !== "archived",
      );

      // Refresh stats
      await fetchStats();
    } catch (error: unknown) {
      console.error("Failed to delete archived notifications:", error);
      throw error;
    }
  };

  /**
   * Delete all notifications by status
   */
  const deleteNotificationsByStatus = async (
    status?: "unread" | "read" | "archived",
  ) => {
    try {
      const params = status ? `?status=${status}` : "";
      await api.delete<{ message: string; count: number }>(
        `/api/notifications/by-status${params}`,
      );

      // Remove from local state
      if (status) {
        notifications.value = notifications.value.filter(
          (n) => n.status !== status,
        );
      } else {
        notifications.value = [];
      }

      // Refresh stats
      await fetchStats();
    } catch (error: unknown) {
      console.error("Failed to delete notifications by status:", error);
      throw error;
    }
  };

  /**
   * Update notification preferences
   */
  const updatePreferences = async (
    input: UpdateNotificationPreferencesInput,
  ) => {
    try {
      const updated = await api.put<NotificationPreferences>(
        "/api/notifications/preferences",
        input,
      );
      preferences.value = updated;
      return updated;
    } catch (error: unknown) {
      console.error("Failed to update notification preferences:", error);
      throw error;
    }
  };

  /**
   * Add notification to local state (called by WebSocket)
   * Prevents duplicates by checking if notification with same ID already exists
   */
  const addNotification = (notification: Notification) => {
    // Prevent duplicates (race condition between WebSocket and manual fetch)
    const exists = notifications.value.some((n) => n.id === notification.id);
    if (exists) {
      return; // Notification already in list, skip
    }

    notifications.value.unshift(notification);

    // Play sound if enabled
    if (
      isSoundEnabled.value &&
      (notification.priority === "high" || notification.priority === "urgent")
    ) {
      playNotificationSound();
    }
  };

  /**
   * Play notification sound
   */
  const playNotificationSound = () => {
    if (typeof window === "undefined") return;

    try {
      const audio = new Audio("/notification.mp3");
      audio.volume = 0.5;
      audio.play().catch(() => {
        // Ignore errors (e.g., autoplay blocked)
      });
    } catch {
      // Ignore errors
    }
  };

  /**
   * Show browser notification (if supported and enabled)
   */
  const showBrowserNotification = async (notification: Notification) => {
    if (typeof window === "undefined" || !("Notification" in window)) return;

    if (!preferences.value?.enableWebPush) return;

    // Request permission if not granted
    if (Notification.permission === "default") {
      await Notification.requestPermission();
    }

    if (Notification.permission === "granted") {
      const browserNotif = new Notification(notification.title, {
        body: notification.message,
        icon: "/logo.png",
        tag: `notification-${notification.id}`,
        requireInteraction: notification.priority === "urgent",
      });

      browserNotif.onclick = () => {
        window.focus();
        if (notification.actionUrl) {
          window.location.href = notification.actionUrl;
        }
        browserNotif.close();
      };
    }
  };

  /**
   * Setup WebSocket listeners for real-time notifications
   */
  const setupWebSocketListeners = () => {
    if (!socketService.socket) return;

    const socket = socketService.socket;

    // Listen for new notifications
    socket.on(
      WSEvent.NOTIFICATION_CREATED,
      (payload: WSEventPayload[typeof WSEvent.NOTIFICATION_CREATED]) => {
        addNotification(payload.notification);
        showBrowserNotification(payload.notification);
      },
    );

    // Listen for notification read updates
    socket.on(
      WSEvent.NOTIFICATION_READ,
      (payload: WSEventPayload[typeof WSEvent.NOTIFICATION_READ]) => {
        const notification = notifications.value.find(
          (n) => n.id === payload.notificationId,
        );
        if (notification) {
          notification.status = "read";
          notification.readAt = new Date().toISOString();
        }
      },
    );

    // Listen for notification deleted events
    socket.on(
      WSEvent.NOTIFICATION_DELETED,
      (payload: WSEventPayload[typeof WSEvent.NOTIFICATION_DELETED]) => {
        notifications.value = notifications.value.filter(
          (n) => n.id !== payload.notificationId,
        );
      },
    );

    // NOTE: Old "notification" and "system:error" events removed
    // All notifications now come via NOTIFICATION_CREATED event (with real DB IDs)
    // This prevents duplicate/temporary notifications with Date.now() IDs
  };

  return {
    // State
    notifications,
    preferences,
    stats,
    isLoading,

    // Getters
    unreadNotifications,
    unreadCount,
    notificationsByType,
    isSoundEnabled,
    sortedNotifications,

    // Actions
    fetchNotifications,
    fetchStats,
    fetchPreferences,
    updateNotification,
    markAsRead,
    markAsArchived,
    archiveNotification,
    markAllAsRead,
    deleteNotification,
    deleteArchivedNotifications,
    deleteNotificationsByStatus,
    updatePreferences,
    addNotification,
    showBrowserNotification,
    setupWebSocketListeners,
  };
});
